<template>
  <div class="nft-page">
    <AppHeader />

    <main class="main">
      <section class="hero">
        <div class="hero-shell">
          <div class="hero-left">
            <div class="hero-topline">
              <span class="hero-badge">{{ $t("nftManage.hero.badge") }}</span>
              <span class="hero-dot"></span>
              <span class="hero-mini">{{ $t("nftManage.hero.mini") }}</span>
            </div>

            <div class="hero-title-wrap">
              <h1 class="hero-title">{{ $t("nftManage.hero.title") }}</h1>
              <p class="hero-desc">
                {{ $t("nftManage.hero.desc") }}
              </p>
            </div>

            <div class="hero-summary-grid">
              <div class="summary-card">
                <div class="k">{{ $t("nftManage.summary.holdCount") }}</div>
                <div class="v">{{ nftOverview.holdCount }}</div>
                <div class="sub">{{ $t("nftManage.summary.holdCountSub") }}</div>
              </div>

              <div class="summary-card">
                <div class="k">{{ $t("nftManage.summary.totalPrincipal") }}</div>
                <div class="v">{{ formatPrice(nftOverview.totalPrincipalUsdt) }}</div>
                <div class="sub">{{ $t("nftManage.summary.totalPrincipalSub") }}</div>
              </div>

              <div class="summary-card">
                <div class="k">{{ $t("nftManage.summary.totalProfit") }}</div>
                <div class="v">{{ formatPrice(nftOverview.totalSettledProfitUsdt) }}</div>
                <div class="sub">{{ $t("nftManage.summary.totalProfitSub") }}</div>
              </div>

              <div class="summary-card">
                <div class="k">{{ $t("nftManage.summary.maxDailyRate") }}</div>
                <div class="v">{{ formatRate(nftOverview.maxDailyRate) }}</div>
                <div class="sub">{{ $t("nftManage.summary.maxDailyRateSub") }}</div>
              </div>
            </div>

            <div v-if="!hasToken" class="hero-tip muted">
              {{ $t("nftManage.tip.needLogin") }}
            </div>
          </div>

          <div class="hero-right">
            <div class="trade-card">
              <div class="trade-head">
                <div>
                  <div class="trade-label">{{ $t("nftManage.buy.title") }}</div>
                  <div class="trade-name">{{ activePlan.name }}</div>
                </div>
                <div class="trade-plan-tag">{{ activePlan.label }}</div>
              </div>

              <div class="trade-select">
                <label class="select-label">{{ $t("nftManage.buy.selectLevel") }}</label>
                <div class="select-wrap">
                  <select v-model="selectedCode" class="select-control" @change="onPlanChange">
                    <option v-for="item in nftPlans" :key="item.code" :value="item.code">
                      {{ item.label }} ｜ {{ formatPrice(item.price) }} ｜ {{ $t("nftManage.buy.dailyIncome") }} {{
                      item.daily }}
                    </option>
                  </select>
                  <span class="select-arrow">▾</span>
                </div>
              </div>

              <div class="trade-price">{{ activePriceText }}</div>

              <div class="trade-meta-grid">
                <div class="trade-mini-card">
                  <div class="mk">{{ $t("nftManage.buy.exitMultiple") }}</div>
                  <div class="mv">{{ activePlan.multiple }}</div>
                </div>
                <div class="trade-mini-card">
                  <div class="mk">{{ $t("nftManage.buy.dailyRate") }}</div>
                  <div class="mv">{{ activePlan.daily }}</div>
                </div>
                <div class="trade-mini-card span2">
                  <div class="mk">{{ $t("nftManage.buy.currentWallet") }}</div>
                  <div class="mv mono">{{ walletConnected ? shortAddr(address) : $t("nftManage.wallet.notConnected") }}
                  </div>
                </div>
              </div>

              <div class="trade-actions">
                <button class="btn ghost" type="button" @click="scrollToAssets">
                  {{ $t("nftManage.buy.viewAssets") }}
                </button>
                <button class="btn primary" type="button" @click="openBuyModal">
                  {{ $t("nftManage.buy.buyNow") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="assets" ref="assetsSec">
        <div class="sec-head">
          <div>
            <div class="sec-mini">{{ $t("nftManage.assets.mini") }}</div>
            <div class="st">{{ $t("nftManage.assets.title") }}</div>
          </div>

          <div class="right muted">
            {{ $t("nftManage.assets.loaded", { loaded: nftAssets.length, total: total }) }}
          </div>
        </div>

        <section class="table-panel">
          <div class="table-head">
            <div>{{ $t("nftManage.table.level") }}</div>
            <div>{{ $t("nftManage.table.no") }}</div>
            <div>{{ $t("nftManage.table.price") }}</div>
            <div>{{ $t("nftManage.table.multiple") }}</div>
            <div>{{ $t("nftManage.table.daily") }}</div>
            <div>{{ $t("nftManage.table.status") }}</div>
            <div class="ta-r">{{ $t("nftManage.table.action") }}</div>
          </div>

          <div class="table-body">
            <div v-if="loadingList" class="table-skeleton">
              <div class="sk" v-for="i in 4" :key="'sk-' + i"></div>
            </div>

            <div v-else-if="!nftAssets.length" class="table-empty">
              {{ hasToken ? $t("nftManage.empty.noData") : $t("nftManage.empty.needLogin") }}
            </div>

            <template v-else v-for="item in nftAssets">
              <div class="table-row" :key="item.id">
                <div class="td">
                  <span class="lv-tag" :class="item.code">{{ item.level }}</span>
                </div>
                <div class="td mono strong-txt">{{ item.id }}</div>
                <div class="td">{{ item.price }}</div>
                <div class="td">{{ item.multiple }}</div>
                <div class="td accent-txt">{{ item.daily }}</div>
                <div class="td">
                  <span class="status-tag" :class="item.status">{{ item.statusText }}</span>
                </div>
                <div class="td ta-r">
                  <button class="text-btn" type="button" @click="toggleDetail(item.id)">
                    {{
                      expandedId === item.id
                        ? $t("nftManage.table.hideDetail")
                        : $t("nftManage.table.viewDetail")
                    }}
                  </button>
                </div>
              </div>

              <div class="detail-row" v-if="expandedId === item.id" :key="item.id + '-detail'">
                <div class="detail-card">
                  <div class="detail-top">
                    <div>
                      <div class="detail-title">{{ $t("nftManage.detail.title") }}</div>
                      <div class="detail-sub">{{ $t("nftManage.detail.sub") }}</div>
                    </div>
                    <div class="detail-no mono">{{ item.id }}</div>
                  </div>

                  <div class="detail-grid">
                    <div class="detail-box">
                      <div class="k">{{ $t("nftManage.detail.currentProfit") }}</div>
                      <div class="v accent">{{ item.currentProfit }}</div>
                    </div>
                    <div class="detail-box">
                      <div class="k">{{ $t("nftManage.detail.remainingProfit") }}</div>
                      <div class="v">{{ item.remainingProfit }}</div>
                    </div>
                    <div class="detail-box">
                      <div class="k">{{ $t("nftManage.detail.createdAt") }}</div>
                      <div class="v">{{ item.createdAt }}</div>
                    </div>
                  </div>

                  <div class="detail-progress">
                    <div class="dp-top">
                      <span>{{ $t("nftManage.detail.progress") }}</span>
                      <b>{{ item.progress }}%</b>
                    </div>
                    <div class="progress-bar">
                      <div class="progress-in" :style="{ width: item.progress + '%' }"></div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div v-if="!loadingList && nftAssets.length" class="loadmore">
              <div class="muted small">
                {{ $t("nftManage.loadmore.loaded", { loaded: nftAssets.length, total: total }) }}
              </div>
              <button class="btn lm" type="button" :disabled="noMore || loadingMore" @click="loadMore">
                {{
                  noMore
                    ? $t("nftManage.loadmore.noMore")
                    : loadingMore
                      ? $t("nftManage.loadmore.loading")
                      : $t("nftManage.loadmore.more")
                }}
              </button>
            </div>
          </div>
        </section>
      </section>

      <FooterLite />
    </main>

    <BottomTab />

    <div class="modal" v-if="buyOpen" @click.self="handleModalMaskClick">
      <div class="modal-card">
        <div class="modal-head">
          <div>
            <div class="modal-title">{{ $t("nftManage.modal.title") }}</div>
            <div class="modal-subtitle">{{ $t("nftManage.modal.sub") }}</div>
          </div>
          <button class="modal-close" type="button" @click="closeBuyModal">×</button>
        </div>

        <div class="modal-product">
          <div class="mp-left">
            <div class="mp-name">{{ activePlan.name }}</div>
            <div class="mp-sub">{{ activePlan.multiple }} · {{ $t("nftManage.buy.dailyIncome") }} {{ activePlan.daily }}
            </div>
          </div>
          <div class="mp-price">{{ activePriceText }}</div>
        </div>

        <div class="modal-info-grid">
          <div class="mi-card span2">
            <div class="k">{{ $t("nftManage.modal.wallet") }}</div>
            <div class="v mono">{{ walletConnected ? shortAddr(address) : "--" }}</div>
          </div>

          <div class="mi-card">
            <div class="k">{{ $t("nftManage.modal.balance") }}</div>
            <div class="v">{{ snapshot ? snapshot.balance + " USDT" : "--" }}</div>
          </div>

          <div class="mi-card">
            <div class="k">{{ $t("nftManage.modal.payAmount") }}</div>
            <div class="v strong">{{ activePriceText }}</div>
          </div>
        </div>

        <div v-if="buyLoading" class="modal-tip">
          {{ $t("nftManage.modal.loading") }}
        </div>

        <div v-if="buyStepText && !buyLocked" class="modal-tip">
          {{ buyStepText }}
        </div>

        <div v-if="buyError" class="modal-err">
          {{ buyError }}
        </div>

        <div v-if="buyLocked" class="modal-success-lock">
          <div class="lock-title">{{ $t("nftManage.modal.successTitle") }}</div>
          <div class="lock-text">{{ $t("nftManage.modal.successText") }}</div>
        </div>

        <div class="modal-actions">
          <button class="btn ghost" type="button" @click="closeBuyModal" :disabled="buySubmitting || buyLocked">
            {{ $t("nftManage.modal.cancel") }}
          </button>

          <button class="btn line" type="button" @click="refreshSnapshot"
            :disabled="buyLoading || buySubmitting || buyLocked">
            {{ $t("nftManage.modal.refresh") }}
          </button>

          <button class="btn primary" type="button" @click="confirmBuy"
            :disabled="buyLoading || buySubmitting || buyLocked">
            {{
              buyLocked
                ? $t("nftManage.modal.creating")
                : buySubmitting
                  ? $t("nftManage.modal.processing")
                  : snapshot && snapshot.approvedEnough
                    ? $t("nftManage.modal.confirmBuy")
                    : $t("nftManage.modal.approveAndBuy")
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from "@/components/AppHeader.vue";
import FooterLite from "@/components/FooterLite.vue";
import BottomTab from "@/components/BottomTab.vue";

import walletStore from "@/store/wallet";
import authReadyMixin from "@/mixins/authReady";

import { getPurchaseSnapshot, placeNftOrder } from "@/services/nftVaultService";
import { fetchNftOverview, fetchNftOrders } from "@/api/nft";

export default {
  name: "NftManage",
  mixins: [authReadyMixin],
  components: { AppHeader, FooterLite, BottomTab },

  data() {
    return {
      wallet: walletStore,

      buyOpen: false,
      buyLocked: false,
      expandedId: "",
      selectedCode: "starter",

      buyLoading: false,
      buySubmitting: false,
      buyError: "",
      buySuccess: "",
      buyStepText: "",
      snapshot: null,

      loadingOverview: false,
      loadingList: false,
      loadingMore: false,
      loadedOnce: false,

      page: 1,
      size: 10,
      total: 0,

      nftOverview: {
        holdCount: 0,
        totalPrincipalUsdt: 0,
        totalSettledProfitUsdt: 0,
        maxDailyRate: 0
      },

      nftAssets: []
    };
  },

  computed: {
    hasToken() {
      return !!(this.wallet && this.wallet.state && this.wallet.state.token);
    },

    activePlan() {
      return this.nftPlans.find((item) => item.code === this.selectedCode) || this.nftPlans[0];
    },

    activeAmountHuman() {
      return this.activePlan ? this.activePlan.price : "0";
    },

    activePriceText() {
      return this.formatPrice(this.activeAmountHuman);
    },

    address() {
      return (this.wallet && this.wallet.state && this.wallet.state.address) || "";
    },

    walletConnected() {
      return !!this.wallet.state.connected && !!this.wallet.state.address;
    },

    noMore() {
      if (Number(this.total || 0) > 0) return this.nftAssets.length >= this.total;
      return this.nftAssets.length < this.size;
    },
    nftPlans() {
      return [
        {
          code: "starter",
          label: this.$t("nftManage.plan.starter.label"),
          name: this.$t("nftManage.plan.starter.name"),
          price: "100",
          multiple: "3X",
          daily: "1.6%"
        },
        {
          code: "middle",
          label: this.$t("nftManage.plan.middle.label"),
          name: this.$t("nftManage.plan.middle.name"),
          price: "500",
          multiple: "3X",
          daily: "1.8%"
        },
        {
          code: "advanced",
          label: this.$t("nftManage.plan.advanced.label"),
          name: this.$t("nftManage.plan.advanced.name"),
          price: "1000",
          multiple: "3X",
          daily: "2.0%"
        },
        {
          code: "ultimate",
          label: this.$t("nftManage.plan.ultimate.label"),
          name: this.$t("nftManage.plan.ultimate.name"),
          price: "50000",
          multiple: "3X",
          daily: "2.2%"
        }
      ];
    }
  },

  mounted() {
    this.tryLoadOnce(false);
  },

  activated() {
    this.tryLoadOnce(false);
  },

  methods: {
    onAuthReady() {
      this.tryLoadOnce(true);
    },

    tryLoadOnce(force) {
      force = !!force;
      if (!this.hasToken) return;
      if (this.loadingOverview || this.loadingList || this.loadingMore) return;
      if (this.loadedOnce && !force) return;

      this.loadedOnce = true;
      this.loadAll(true);
    },

    unwrap(res) {
      if (res && res.data && typeof res.data === "object") {
        if ("data" in res.data) return res.data.data;
        return res.data;
      }
      if (res && typeof res === "object" && "data" in res) return res.data;
      return res;
    },

    async loadAll(silent) {
      silent = !!silent;
      if (!this.hasToken) return;

      this.loadingOverview = true;
      this.loadingList = true;

      try {
        const oRes = await fetchNftOverview({ silent: silent });
        const ov = this.unwrap(oRes) || {};

        this.nftOverview = {
          holdCount: Number(ov.holdCount || 0),
          totalPrincipalUsdt: Number(ov.totalPrincipalUsdt || 0),
          totalSettledProfitUsdt: Number(ov.totalSettledProfitUsdt || 0),
          maxDailyRate: Number(ov.maxDailyRate || 0)
        };

        this.page = 1;
        await this.loadOrders(true, true);
      } catch (e) {
        if (!silent) this.toast((e && e.message) || this.$t("nftManage.toast.loadFailed"));
      } finally {
        this.loadingOverview = false;
        this.loadingList = false;
      }
    },

    async loadOrders(reset, silent) {
      reset = !!reset;
      silent = !!silent;

      if (!this.hasToken) return;

      const page = reset ? 1 : this.page;
      const size = this.size;

      const res = await fetchNftOrders({ page: page, size: size }, { silent: silent });
      const vo = this.unwrap(res) || {};

      const list = vo.records || vo.list || vo.items || (Array.isArray(vo) ? vo : []);
      const total = Number(vo.total || 0);
      const mapped = (list || []).map(this.mapNftOrder);

      if (reset) this.nftAssets = mapped;
      else this.nftAssets = this.nftAssets.concat(mapped);

      this.total = total || this.nftAssets.length;
      this.expandedId = "";
    },

    async loadMore() {
      if (!this.hasToken) return this.toast(this.$t("nftManage.toast.needLogin"));
      if (this.noMore) return;
      if (this.loadingMore || this.loadingList) return;

      this.loadingMore = true;
      try {
        this.page = this.page + 1;
        await this.loadOrders(false, true);
      } catch (e) {
        this.page = Math.max(1, this.page - 1);
        this.toast((e && e.message) || this.$t("nftManage.toast.loadMoreFailed"));
      } finally {
        this.loadingMore = false;
      }
    },

    mapNftOrder(item) {
      item = item || {};

      return {
        id: String(item.orderNo || item.id || ""),
        code: this.mapLevelCode(item.nftLevel),
        level: String(item.nftName || "--") + " NFT",
        price: this.formatPrice(item.principalUsdt || 0),
        multiple: this.formatMultiple(item.exitMultiple),
        daily: this.formatRate(item.dailyRate),
        status: this.mapStatusClass(item.status),
        statusText: item.statusText || this.mapStatusText(item.status),
        currentProfit: this.formatPrice(item.settledProfitUsdt || 0),
        remainingProfit: this.formatPrice(item.remainingUsdt || 0),
        progress: Number(item.progress || 0).toFixed(1),
        createdAt: item.createTime || "--"
      };
    },

    mapLevelCode(level) {
      switch (Number(level || 0)) {
        case 1:
          return "starter";
        case 2:
          return "middle";
        case 3:
          return "advanced";
        case 4:
          return "ultimate";
        default:
          return "starter";
      }
    },

    mapStatusClass(status) {
      switch (Number(status || 0)) {
        case 0:
          return "pending";
        case 1:
          return "running";
        case 2:
          return "done";
        case 3:
          return "cancel";
        default:
          return "running";
      }
    },

    mapStatusText(status) {
      switch (Number(status || 0)) {
        case 0:
          return this.$t("nftManage.status.pending");
        case 1:
          return this.$t("nftManage.status.running");
        case 2:
          return this.$t("nftManage.status.done");
        case 3:
          return this.$t("nftManage.status.cancel");
        default:
          return this.$t("nftManage.status.unknown");
      }
    },

    formatPrice(v) {
      return (
        Number(v || 0).toLocaleString(undefined, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        }) + " USDT"
      );
    },

    formatRate(v) {
      return (Number(v || 0) * 100).toFixed(1) + "%";
    },

    formatMultiple(v) {
      return Number(v || 0).toFixed(0) + "X";
    },

    resetBuyState() {
      this.buyLoading = false;
      this.buySubmitting = false;
      this.buyError = "";
      this.buySuccess = "";
      this.buyStepText = "";
      this.snapshot = null;
      this.buyLocked = false;
    },

    closeBuyModal() {
      this.buyOpen = false;
      this.$nextTick(() => {
        this.resetBuyState();
      });
    },

    handleModalMaskClick() {
      if (this.buySubmitting) return;
      this.closeBuyModal();
    },

    onPlanChange() {
      this.buyError = "";
      this.buySuccess = "";
      this.buyStepText = "";

      if (this.buyOpen && this.walletConnected && !this.buyLocked) {
        this.refreshSnapshot();
      }
    },

    async openBuyModal() {
      this.resetBuyState();

      if (!this.walletConnected) {
        this.wallet.openModal();
        return;
      }

      this.buyOpen = true;
      await this.refreshSnapshot();
    },

    async refreshSnapshot() {
      if (!this.walletConnected || this.buyLocked) return;

      this.buyLoading = true;
      this.buyError = "";

      try {
        this.snapshot = await getPurchaseSnapshot(this.address, this.activeAmountHuman);
      } catch (e) {
        this.buyError = (e && e.message) || this.$t("nftManage.toast.snapshotFailed");
      } finally {
        this.buyLoading = false;
      }
    },

    async confirmBuy() {
      if (!this.walletConnected) {
        this.wallet.openModal();
        return;
      }

      if (this.buyLocked) return;

      this.buySubmitting = true;
      this.buyError = "";
      this.buySuccess = "";
      this.buyStepText = "";

      try {
        await placeNftOrder({
          amountHuman: this.activeAmountHuman,
          onStep: (step, payload) => {
            switch (step) {
              case "SNAPSHOT_READY":
                this.buyStepText = this.$t("nftManage.step.snapshotReady");
                break;
              case "APPROVE_REQUIRED":
                this.buyStepText = this.$t("nftManage.step.approveRequired");
                break;
              case "APPROVE_TX":
                this.buyStepText = this.$t("nftManage.step.approveTx") + payload.hash;
                break;
              case "APPROVE_CONFIRMED":
                this.buyStepText = this.$t("nftManage.step.approveConfirmed");
                break;
              case "APPROVE_SKIPPED":
                this.buyStepText = this.$t("nftManage.step.approveSkipped");
                break;
              case "BUY_SENDING":
                this.buyStepText = this.$t("nftManage.step.buySending");
                break;
              case "BUY_TX":
                this.buyStepText = this.$t("nftManage.step.buyTx") + payload.hash;
                break;
              case "BUY_CONFIRMED":
                this.buyStepText = "";
                this.buyLocked = true;
                break;
              default:
                this.buyStepText = step;
            }
          }
        });

        this.buyLocked = true;

        await this.loadAll(true);
      } catch (e) {
        this.buyError = (e && e.message) || this.$t("nftManage.toast.buyFailed");
      } finally {
        this.buySubmitting = false;
      }
    },

    toggleDetail(id) {
      this.expandedId = this.expandedId === id ? "" : id;
    },

    scrollToAssets() {
      this.$nextTick(() => {
        const el = this.$refs.assetsSec;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 12;
        window.scrollTo({ top: top, behavior: "smooth" });
      });
    },

    shortAddr(v) {
      if (!v) return "--";
      return v.slice(0, 6) + "..." + v.slice(-4);
    },

    toast(msg) {
      this.$toast && this.$toast.show ? this.$toast.show(msg) : alert(msg);
    }
  }
};
</script>

<style scoped>
.nft-page {
  min-height: 100vh;
  color: #eef4ff;
  background:
    radial-gradient(circle at 12% 0%, rgba(255, 170, 76, 0.10), transparent 26%),
    radial-gradient(circle at 100% 0%, rgba(66, 121, 255, 0.10), transparent 30%),
    linear-gradient(180deg, #040814 0%, #030611 52%, #02050d 100%);
}

.main {
  max-width: 1220px;
  margin: 0 auto;
  padding: 22px 16px 34px;
}

/* =========================
   通用容器
========================= */
.hero-shell,
.table-panel,
.modal-card {
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: linear-gradient(180deg, rgba(9, 18, 36, 0.92), rgba(5, 11, 24, 0.96));
  border-radius: 28px;
  box-shadow:
    0 14px 48px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
}

.hero-shell {
  display: grid;
  grid-template-columns: 1.12fr 0.88fr;
  gap: 18px;
  padding: 22px;
}

/* =========================
   Hero
========================= */
.hero-topline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.hero-badge,
.trade-plan-tag,
.lv-tag,
.status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.02em;
}

.hero-badge,
.trade-plan-tag {
  background: linear-gradient(180deg, rgba(255, 177, 75, 0.14), rgba(255, 141, 44, 0.08));
  border: 1px solid rgba(255, 178, 76, 0.28);
  color: #ffd89f;
  box-shadow: inset 0 0 18px rgba(255, 178, 76, 0.04);
}

.status-tag.running {
  background: linear-gradient(180deg, rgba(255, 177, 75, 0.14), rgba(255, 141, 44, 0.08));
  border: 1px solid rgba(255, 178, 76, 0.26);
  color: #ffd89f;
}

.status-tag.pending {
  background: rgba(99, 149, 255, 0.10);
  border: 1px solid rgba(99, 149, 255, 0.24);
  color: #bfd3ff;
}

.status-tag.done {
  background: rgba(56, 201, 124, 0.10);
  border: 1px solid rgba(56, 201, 124, 0.24);
  color: #bbffd6;
}

.status-tag.cancel {
  background: rgba(255, 103, 103, 0.10);
  border: 1px solid rgba(255, 103, 103, 0.22);
  color: #ffd0d0;
}

.hero-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 214, 154, 0.85);
  box-shadow: 0 0 10px rgba(255, 184, 88, 0.7);
}

.hero-mini {
  font-size: 12px;
  color: rgba(201, 214, 236, 0.52);
  letter-spacing: 0.06em;
}

.hero-title-wrap {
  margin-top: 18px;
}

.hero-title {
  margin: 0;
  font-size: 40px;
  line-height: 1.08;
  font-weight: 1000;
  letter-spacing: -0.02em;
  color: #f4f8ff;
}

.hero-desc {
  margin-top: 12px;
  max-width: 720px;
  font-size: 14px;
  line-height: 1.8;
  color: rgba(196, 210, 232, 0.68);
}

.hero-tip {
  margin-top: 14px;
  font-size: 12px;
}

.hero-summary-grid,
.detail-grid,
.modal-info-grid,
.trade-meta-grid {
  display: grid;
  gap: 12px;
}

.hero-summary-grid {
  margin-top: 22px;
  grid-template-columns: 1fr 1fr;
}

.summary-card,
.detail-box,
.mi-card,
.trade-mini-card {
  padding: 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);
}

.k,
.mk {
  font-size: 12px;
  color: rgba(178, 193, 220, 0.60);
}

.v,
.mv {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 950;
  color: #f4f8ff;
}

.sub {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(173, 188, 211, 0.45);
}

/* =========================
   购买卡片
========================= */
.trade-card {
  height: 100%;
  padding: 20px;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    radial-gradient(circle at top right, rgba(255, 177, 75, 0.08), transparent 26%),
    linear-gradient(180deg, rgba(255, 255, 255, .04), rgba(255, 255, 255, .02));
}

.trade-head,
.detail-top,
.modal-head,
.modal-product {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.trade-label {
  font-size: 12px;
  color: rgba(182, 197, 220, 0.56);
}

.trade-name,
.mp-name {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 980;
  color: #f5f8ff;
}

.trade-select {
  margin-top: 18px;
}

.select-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  color: rgba(182, 197, 220, 0.56);
}

.select-wrap {
  position: relative;
}

.select-control {
  width: 100%;
  height: 54px;
  border-radius: 16px;
  padding: 0 44px 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(10, 18, 33, 0.88);
  color: #eef4ff;
  font-size: 15px;
  font-weight: 900;
  outline: none;
}

.select-control:focus {
  border-color: rgba(255, 178, 76, 0.36);
  box-shadow: 0 0 0 3px rgba(255, 178, 76, 0.08);
}

.select-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, .7);
}

.trade-price,
.mp-price {
  margin-top: 18px;
  font-size: 42px;
  line-height: 1;
  font-weight: 1000;
  color: #ffd79f;
  letter-spacing: -0.03em;
  text-shadow: 0 0 18px rgba(255, 178, 76, 0.08);
}

.trade-meta-grid {
  margin-top: 18px;
  grid-template-columns: 1fr 1fr;
}

.span2 {
  grid-column: 1 / -1;
}

.trade-actions,
.modal-actions {
  margin-top: 18px;
  display: flex;
  gap: 10px;
}

/* =========================
   NFT 持仓管理区
========================= */
.assets {
  margin-top: 24px;
}

.sec-head {
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-bottom: 12px;
  gap: 12px;
}

.sec-mini {
  font-size: 12px;
  color: rgba(160, 178, 205, 0.46);
  letter-spacing: 0.10em;
  text-transform: uppercase;
}

.st {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 980;
  color: #f3f7ff;
}

.right {
  font-size: 12px;
  color: rgba(183, 198, 223, 0.56);
}

.table-panel {
  overflow: hidden;
  position: relative;
}

.table-panel::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.10), transparent);
  pointer-events: none;
}

.table-head,
.table-row {
  display: grid;
  grid-template-columns: 1.2fr 2.1fr 1.2fr 0.9fr 1fr 1fr 0.8fr;
  gap: 10px;
  align-items: center;
}

.table-head {
  padding: 16px 18px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(150, 170, 200, 0.58);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.015));
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.table-row {
  padding: 18px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
  transition: background .22s ease, transform .22s ease, box-shadow .22s ease;
}

.table-row:hover {
  background:
    linear-gradient(90deg, rgba(255, 178, 76, 0.035), rgba(255, 255, 255, 0.012));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);
}

.td {
  color: rgba(205, 218, 238, 0.84);
  font-size: 14px;
  line-height: 1.45;
}

.strong-txt {
  color: #f5f8ff;
  font-weight: 900;
  letter-spacing: 0.01em;
}

.accent,
.accent-txt {
  color: #ffd79f;
  font-weight: 900;
}

.lv-tag.starter {
  background: rgba(255, 178, 76, 0.10);
  border: 1px solid rgba(255, 178, 76, 0.24);
  color: #ffd79f;
}

.lv-tag.middle {
  background: rgba(92, 138, 255, 0.10);
  border: 1px solid rgba(92, 138, 255, 0.22);
  color: #c8d8ff;
}

.lv-tag.advanced {
  background: rgba(112, 87, 255, 0.12);
  border: 1px solid rgba(112, 87, 255, 0.24);
  color: #d8d0ff;
}

.lv-tag.ultimate {
  background: linear-gradient(180deg, rgba(255, 188, 95, 0.16), rgba(255, 125, 50, 0.10));
  border: 1px solid rgba(255, 184, 88, 0.30);
  color: #fff1d5;
  box-shadow: 0 0 18px rgba(255, 178, 76, 0.08);
}

.detail-row {
  padding: 0 18px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.008), rgba(255, 255, 255, 0));
}

.detail-card {
  border-radius: 20px;
  padding: 18px;
  background:
    radial-gradient(circle at right top, rgba(255, 178, 76, 0.06), transparent 30%),
    linear-gradient(180deg, rgba(8, 17, 34, 0.94), rgba(6, 12, 24, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.02),
    0 10px 28px rgba(0, 0, 0, 0.16);
}

.detail-title {
  font-size: 18px;
  font-weight: 950;
  color: #f4f8ff;
}

.detail-sub {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(174, 190, 216, 0.52);
}

.detail-no {
  color: rgba(210, 220, 238, 0.72);
  font-size: 12px;
}

.detail-grid {
  margin-top: 16px;
  grid-template-columns: repeat(4, 1fr);
}

.detail-box .k {
  color: rgba(168, 184, 210, 0.58);
}

.detail-box .v {
  color: #eef4ff;
  font-size: 18px;
}

.detail-box .v.accent {
  color: #ffd79f;
}

.detail-progress {
  margin-top: 16px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.dp-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(205, 218, 238, 0.82);
}

.dp-top b {
  color: #ffd79f;
}

.progress-bar {
  margin-top: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.24);
}

.progress-in {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 178, 76, .98), rgba(255, 140, 47, .92));
  box-shadow: 0 0 14px rgba(255, 172, 60, 0.18);
}

/* =========================
   骨架 / 空状态 / 加载更多
========================= */
.table-skeleton {
  padding: 14px 18px 18px;
}

.table-skeleton .sk {
  height: 66px;
  border-radius: 18px;
  background: linear-gradient(90deg, rgba(255, 255, 255, .03), rgba(255, 255, 255, .045), rgba(255, 255, 255, .03));
  border: 1px solid rgba(255, 255, 255, .06);
}

.table-skeleton .sk+.sk {
  margin-top: 10px;
}

.table-empty {
  padding: 32px 18px;
  text-align: center;
  color: rgba(184, 199, 221, 0.62);
  font-size: 13px;
}

.loadmore {
  padding: 14px 18px 18px;
  border-top: 1px solid rgba(255, 255, 255, .05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: nowrap;
}

.loadmore>div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =========================
   按钮
========================= */
.btn.lm {
  flex: 0 0 auto;
  height: 38px;
  line-height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .10);
  color: rgba(240, 244, 252, .86);
  white-space: nowrap;
}

.btn {
  height: 46px;
  border-radius: 14px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  font-size: 14px;
  font-weight: 950;
  cursor: pointer;
  transition: all .22s ease;
}

.btn.primary {
  color: #1a1207;
  background: linear-gradient(135deg, rgba(255, 178, 76, .98), rgba(255, 140, 47, .92));
  box-shadow: 0 10px 20px rgba(255, 147, 48, 0.12);
}

.btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(255, 147, 48, 0.16);
}

.btn.ghost,
.btn.line {
  color: #edf3ff;
  background: rgba(255, 255, 255, 0.045);
}

.btn.ghost:hover,
.btn.line:hover,
.btn.lm:hover {
  background: rgba(255, 255, 255, .07);
}

.btn[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none !important;
}

.text-btn {
  border: none;
  background: transparent;
  color: #ffd79f;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  transition: color .2s ease, opacity .2s ease;
}

.text-btn:hover {
  color: #ffe6bd;
}

.ta-r {
  text-align: right;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace;
}

/* =========================
   Modal
========================= */
.modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(3, 6, 17, 0.76);
  backdrop-filter: blur(14px);
}

.modal-card {
  width: min(560px, 100%);
  padding: 20px;
}

.modal-title {
  font-size: 24px;
  font-weight: 980;
  color: #f4f8ff;
}

.modal-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(178, 194, 219, 0.50);
}

.modal-close {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, .10);
  background: rgba(255, 255, 255, .05);
  color: #fff;
  font-size: 22px;
  cursor: pointer;
}

.modal-info-grid {
  margin-top: 16px;
  grid-template-columns: 1fr 1fr;
}

.modal-tip,
.modal-err,
.modal-success-lock {
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.7;
}

.modal-tip {
  color: rgba(235, 241, 252, .84);
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(255, 255, 255, .08);
}

.modal-err {
  color: #ffd4d4;
  background: rgba(255, 73, 73, .08);
  border: 1px solid rgba(255, 73, 73, .18);
}

.modal-success-lock {
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 178, 76, .12), rgba(255, 140, 47, .08));
  border: 1px solid rgba(255, 178, 76, .24);
}

.lock-title {
  font-size: 20px;
  font-weight: 1000;
  color: #ffd79f;
  letter-spacing: 0.02em;
}

.lock-text {
  margin-top: 10px;
  font-size: 18px;
  font-weight: 900;
  color: #fff4de;
  line-height: 1.6;
}

.strong {
  color: #ffd79f;
}

/* =========================
   响应式
========================= */
@media (max-width: 1100px) {
  .hero-shell {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {

  .hero-summary-grid,
  .detail-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .main {
    padding: 14px 12px 24px;
  }

  .hero-title {
    font-size: 30px;
  }

  .hero-summary-grid,
  .detail-grid,
  .modal-info-grid,
  .trade-meta-grid {
    grid-template-columns: 1fr;
  }

  .table-head {
    display: none;
  }

  .table-row {
    grid-template-columns: 1fr 1fr;
    padding: 14px;
    gap: 12px;
  }

  .td {
    font-size: 13px;
  }

  .detail-row {
    padding: 0 14px 14px;
  }

  .ta-r {
    text-align: left;
  }

  .trade-actions,
  .modal-actions {
    flex-direction: column;
  }

  .trade-price,
  .mp-price {
    font-size: 34px;
  }

  .lock-text {
    font-size: 16px;
  }

  .loadmore {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>